import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { toast } from 'react-toastify';
import { IEntryType } from '../../ObjectTypes/SetupTypes';
import StoreX, { store } from '../../redux/oldStore';
import { ProjectSummary } from '../../utils/CommonTypes/ProjectSummaryTypes';
import { ServerResponse } from '../../utils/Server';
import Icon, { IconType } from '../Icon/Icon';
import RegistrationStepper from '../RegistrationStepper/RegistrationStepper';
import Tools from '../JudgingDashboard/Tools';
import { IState, ReduxMap } from '../../redux/redux';
import { connect } from 'react-redux';

const FormFromApiData = lazy(() => import('../FormFromApiData/FormFromApiData'));

interface UserProjectDisplayProps extends IState {
  AlwaysShow: boolean;
  UserId: string | undefined;
}

const UserProjectDisplay = (props: UserProjectDisplayProps) => {
    const { userId, targetId, entryType } = useParams();
    const stepperPersonId = props.UserId ?? userId;
    const navigate = useNavigate();
    const location = useLocation();
    const [info, setInfo] = useState<IPersonsProjectResponse>();
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [chosenProject, setChosenProject] = useState<ProjectSummary>();
    const [teamKey, setTeamKey] = useState('');

    useEffect(() => {
        if ((userId && !targetId) || props.AlwaysShow) {
            getInfo();
        }
    }, []);

    const getInfo = async () => {
        store.server
            .postApi<ServerResponse<IPersonsProjectResponse>>('../Person/Projects', {
                userId: props.UserId ?? userId,
            })
            .then((res) => {
                if (res.Success) {
                    let projects = res.Value.Projects;
                    if (res.Value.UsesEntryTypes && res.Value.EntryTypes && res.Value.EntryTypes.length > 0) {
                        res.Value.EntryTypes.forEach((e) => {
                            e.NumberOfProjects = projects.filter((f) => f.EntryTypePublicId === e.PublicId).length;
                        });
                    }

                    setInfo(res.Value);
                    setProjects(res.Value.Projects);
                    if (res.Value && res.Value.Projects && res.Value.Projects.length > 0 && !props.AlwaysShow) {
                        selectProject(res.Value.Projects[0]);
                    }
                    if (res.Value && res.Value.Projects.length == 0 && !res.Value.UsesEntryTypes && !res.Value.TeamProjectsAllowed) {
                        NewProjectClick();
                        return;
                    }
                    let teamKey = StoreX.GetQueryStringValue('team');
                    if (StoreX.GetQueryStringValue('team')) {
                        let project = res.Value.Projects.find((x) => x.Id === teamKey);
                        if (project) {
                            selectProject(project);
                        }
                    }
                } else {
                    toast.error(res.Message);
                }
            });
    };

    const selectProject = (p: ProjectSummary) => {
        if (!p) return;

        if (info?.AllowMultipleEntries) {
            let url = `/app/project/${props.UserId ?? userId}/${p.Id}`;
            //window.location.pathname = url;
            navigate(url);
        } else {
            navigate(`/app/project/${userId}/${p.Id}/${location.search}`);
        }
    };

    const removeProject = (p: ProjectSummary) => {
        if (!p) return;
        if (!window.confirm(`Are you sure you want to remove this entry? This cannot be undone.`)) {
            return;
        }

        store.server
            .postApi<ServerResponse<IPersonsProjectResponse>>('../Person/RemoveEntry', {
                personId: props.UserId ?? userId,
                projectKey: p.Id,
            })
            .then((res) => {
                if (res.Success) {
                    getInfo();
                } else {
                    toast.error(res.Message);
                }
            });
    };

    const unlinkProject = (p: ProjectSummary) => {
        if (!p) return;
        const title = p.Title || p.ProjectId || 'this entry';
        if (!window.confirm(`Unlink from ${title}? You will be detached from this team entry.`)) {
            return;
        }

        store.server
            .postApi<ServerResponse<unknown>>('../Person/UnlinkProject', {
                personId: props.UserId ?? userId,
            })
            .then((res) => {
                if (res.Success) {
                    toast.success('Entry unlinked.');
                    getInfo();
                } else {
                    toast.error(res.Message || 'Unable to unlink from this entry.', { autoClose: false });
                }
            });
    };

    useEffect(() => {
        if (projects?.length === 1 && !props.AlwaysShow) {
            setChosenProject(projects[0]);
        }
    }, [projects]);

    const entryStatus = (p: ProjectSummary) => {
        const submissionBadge = props.Settings?.EnableEntrySubmission ? (
            p.IsSubmitted ? (
                <span className="entry-status entry-status-submitted" title={p.SubmittedAtUtc ? `Submitted ${p.SubmittedAtUtc}` : 'Submitted'}>
                    Submitted
                </span>
            ) : (
                <span className="entry-status entry-status-not-submitted" title="Not submitted">
                    Not submitted
                </span>
            )
        ) : null;

        let status = <></>;
        switch (p.Status) {
            case 'Approved':
                status = <span className="entry-status">{props.Settings?.text.EntryStatusApproved}</span>;
                break;
            case 'Dropped':
                status = <span className="entry-status">{props.Settings?.text.EntryStatusDropped}</span>;
                break;
            case 'DoNotAdvance':
                status = (
                    <span className="entry-status" title={'Approved, Do not advance'}>
                        {props.Settings?.text.EntryStatusDNA}
                    </span>
                );
                break;
            case 'Pending':
            default:
                status = <></>;
        }
        return (
            <>
                {submissionBadge}
                {status}
            </>
        );
    };

    const entryStatusClass = (p: ProjectSummary) => {
        switch (p.Status) {
            case 'Approved':
                return 'entry-status-approved';
            case 'Dropped':
                return 'entry-status-dropped';
            case 'DoNotAdvance':
                return 'entry-status-dna';
            case 'Pending':
            default:
                return '';
        }
    };

    // TODO:
    // most clients will only have 1 project enabled
    // if < 1 project, create a project.
    // if === 1 project, just display the project information
    // if > 1 project, display a project selector
    const mappedProjects = projects?.map((p, i) => {
        let hasProjectId = p.ProjectId ? true : false;
        let entryType = info?.EntryTypes?.find((x) => x.PublicId === p.EntryTypePublicId);
        return (
            <div className={`entry-button ${entryStatusClass(p)} ${p.IsNew ? 'new' : ''}`} key={i}>
                {entryStatus(p)}
                <h3>
                    <Icon type={IconType.project} /> {hasProjectId ? <>{p.ProjectId}</> : p.Title ? <>{p.Title}</> : <>Untitled Project</>}
                </h3>
                <div className="small">{p.Category}</div>
                <hr />
                {hasProjectId && (
                    <>
                        <div>{p.Title}</div>
                        <hr />
                    </>
                )}
                <div className="flex-between">
                    <div>{entryType && <>
                        <span>{entryType.Name}</span>
                        {entryType.Frozen && (
                            <div>
                                <span className="label label-warning">Data Frozen</span>
                            </div>
                        )}
                    </>}</div>
                    <div className="">
                        {info?.AllowSelfLinkUnlink && !info?.EntryLocked && !p.IsNew && (
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    unlinkProject(p);
                                }}>
                                <Icon type={IconType.chainBroken} /> Unlink
                            </button>
                        )}
                        {info?.AllowMultipleEntries && info?.EntriesCanBeRemoved && (
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    removeProject(p);
                                }}>
                                <Icon type={IconType.trashO} /> Remove
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                selectProject(p);
                            }}>
                            <Icon type={IconType.projectNew} /> Select
                        </button>
                    </div>
                </div>
            </div>
        );
    });

    const handleLinkTeamProjectClick = () => {
        let submitObj = {
            personId: props.UserId ?? userId,
            projectId: teamKey,
            addToUser: info?.AllowMultipleEntries,
        };

        if (!submitObj.projectId) {
            toast.warning(`The ${StoreX.Text.LabelProjectKey} you provided is not valid.`);
            return;
        }

        store.server.postApi<ServerResponse<any>>('../Person/LinkProject', submitObj).then((res) => {
            if (res.Success) {
                toast.success('Linked!');
                getInfo();
                if (!info?.AllowMultipleEntries || true) {
                    navigate(`/app/project/${props.UserId || userId}/${submitObj.projectId}/${location.search}`);
                }
            } else {
                console.log(res);
                toast.error(res.Message, { autoClose: false });
            }
        });
    };

    const NewProjectClick = () => {
        if (info?.AllowMultipleEntries) {
            let url = `/app/project/${props.UserId ?? userId}/${StoreX.NewGuid()}`;
            window.location.pathname = url;
        } else {
            navigate(`/app/project/${userId}/${StoreX.NewGuid()}/${location.search}`);
        }
    };

    const NewProjectOfTypeClick = (type: IEntryType) => {
        if (type.LimitOfEntriesPerUser && (type.NumberOfProjects ?? 0) >= type.LimitOfEntriesPerUser) {
            toast.warning(`You already have the max number of these entry types.`);
            return;
        }

        let url = `/app/project/${props.UserId ?? userId}/${StoreX.NewGuid()}/${type.PublicId}`;
        window.location.pathname = url;
    };

    return (
        <div className="bumper-l">
            {(!targetId || props.AlwaysShow) && (
                <>
                    {projects && projects.length > 0 && (
                        <div>
                            <h3>
                                <Icon type={IconType.project} /> {info?.TextMyEntriesHeader}
                            </h3>

                            <div className="flex-evenly wrap">{mappedProjects}</div>

                            {chosenProject && <></>}
                        </div>
                    )}
                    <div className="">
                        {!info ? (
                            <div className="bumper">Loading entries...</div>
                        ) : (
                            <>
                        <h3>
                            <Icon type={IconType.project} /> {info.TextNewEntryHeader}
                        </h3>
                        {info.TeamProjectsAllowed || info.AllowSelfLinkUnlink ? <span>To start or link to an entry click an option below.</span> : <span>To start an entry click an option below.</span>}

                        <div className="flex-evenly wrap">
                            {!info.UsesEntryTypes && !info.EntryLocked && (
                                <div className="entry-button" title="click to start a new entry">
                                    <h3>
                                        <Icon type={IconType.projectNew} /> Start a {info.TextNewEntryHeader}
                                    </h3>
                                    <hr />
                                    <div className="max-height-150">Choose this option if you are working alone or you are the first member of your team to register.</div>
                                    <hr />
                                    <div className="text-right">
                                        <button className="btn btn-default new-entry-btn" onClick={NewProjectClick}>
                                            <Icon type={IconType.projectNew} /> Start
                                        </button>
                                    </div>
                                </div>
                            )}
                            {info.UsesEntryTypes && !info.EntryLocked && (
                                <>
                                    {info.EntryTypes.map((x, i) => {
                                        return (
                                            <div className="entry-button" title={`click to start a new "${x.Name}" entry`} key={`entry-type-panel-${i}`}>
                                                <h3>
                                                    <Icon type={IconType.projectNew} /> {x.Name}
                                                </h3>
                                                <hr />
                                                <div className="max-height-150" dangerouslySetInnerHTML={{ __html: x.Description ?? '' }}></div>
                                                <hr />
                                                <div className="flex-between">
                                                    <div>
                                                        {x.LimitOfEntriesPerUser && x.LimitOfEntriesPerUser > 0 && (
                                                            <span>
                                                                {x.NumberOfProjects} of {x.LimitOfEntriesPerUser}
                                                            </span>
                                                        )}

                                                        {x.Frozen && (
                                                            <div>
                                                                <span className="label label-warning">Data Frozen</span>
                                                            </div>
                                                        )}
                                                        {(x.Closed || !x.Open) && (
                                                            <div>
                                                                <span className="label label-danger">Closed</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="">
                                                        {x.Open && (
                                                            <button
                                                                className={`btn btn-default new-entry-btn type-${x.Id}`}
                                                                onClick={() => {
                                                                    NewProjectOfTypeClick(x);
                                                                }}>
                                                                <Icon type={IconType.projectNew} /> Start
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                            {(info.TeamProjectsAllowed || (info.AllowSelfLinkUnlink && !info.EntryLocked)) && (
                                <div className="entry-button" title="click to link to an existing entry">
                                    <h3>
                                        <Icon type={IconType.link} /> {info.TextLinkToExistingEntryHeader}
                                    </h3>
                                    <hr />
                                    <div className="max-height-150">Choose this option if you are working with a team and they have already registered.</div>
                                    <hr />
                                    <div className="form-inline text-right">
                                        <div className="form-group">
                                            <div className="input-group">
                                                <div className="input-group-addon">Project Key</div>
                                                <input
                                                    type="text"
                                                    name="team-project-link"
                                                    id="team-project-link"
                                                    className="form-control"
                                                    placeholder={StoreX.Text.LabelProjectKey}
                                                    value={teamKey}
                                                    onChange={(e) => setTeamKey(e.target.value)}
                                                />
                                            </div>
                                            <button className="btn btn-default" onClick={handleLinkTeamProjectClick}>
                                                <Icon type={IconType.link} /> Link
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                            </>
                        )}
                    </div>
                </>
            )}
            <div>
                {info?.AllowMultipleEntries}
                {targetId && !props.AlwaysShow && (
                    <Suspense fallback={<div className="bumper">Loading form...</div>}>
                    <FormFromApiData
                        getInfoRoute={'../Project/MainForm'}
                        saveRoute={'../Project/SaveForm'}
                        idCalledInApi="projectId"
                        isNew={false} //this is the default value...
                        completionStepKey="project"
                    />
                    </Suspense>
                )}
            </div>
            {!props.AlwaysShow && !targetId && stepperPersonId && (
                <RegistrationStepper personId={stepperPersonId} currentStep="project" />
            )}
      </div>
  );
};

const mapStateToProps = (state) => {
  return { ...state.main };
};

export default connect(mapStateToProps)(UserProjectDisplay);

interface IPersonsProjectResponse {
  Projects: ProjectSummary[];
  InformationFrozen: boolean;
  EntryLocked: boolean;
  AllowMultipleEntries: boolean;
  EntriesCanBeRemoved: boolean;
  TeamProjectsAllowed: boolean;
  AllowSelfLinkUnlink: boolean;
  UsesEntryTypes: boolean;
  EntryTypes: IEntryType[];
  TextLinkToExistingEntryHeader: string;
  TextMyEntriesHeader: string;
  TextNewEntryHeader: string;
}
